/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import l10n from '../../shared/strings';
import { LISTING_STATUS } from './constants/listingStatus';
import { pruneSelection } from './helpers/applySelection';
import { getChannelEntryId } from './helpers/channelHelpers';
import { upsertListing, upsertListingVariant, removeListingVariant } from './helpers/entityAdapter';
import removeListingChannelEntities from './helpers/removeListingChannelEntities';
import { setActiveTabByProductChannel } from './helpers/uiAdapter';

const strings = l10n.ACTIONS_TOOLBAR.PRODUCTS;
const SKIP_UNAVAILABLE = 'Not an available listing';
const SKIP_UNAVAILABLE_VARIANT = 'Not an available listing variant';
const SKIP_NO_CHANGES = 'Listing has no draft changes';
const SKIP_NO_VARIANT_CHANGES = 'Listing variant has no draft changes';

const toCount = (value) => {
  if (Array.isArray(value)) return value.length;
  return Number(value) || 0;
};

const toId = (value) => String(value?.id ?? value);

const isUnavailableSkip = (reason) => (
  reason === SKIP_UNAVAILABLE || reason === SKIP_UNAVAILABLE_VARIANT
);

const isNoChangeSkip = (reason) => (
  reason === SKIP_NO_CHANGES || reason === SKIP_NO_VARIANT_CHANGES
);

const restorePublishedFromLive = (entity) => {
  const live = entity.live && typeof entity.live === 'object' ? entity.live : {};
  const restored = {
    ...entity,
    ...live,
    _id: entity._id,
    type: entity.type,
    masterProductId: entity.masterProductId,
    baseProductId: entity.baseProductId,
    masterVariantId: entity.masterVariantId,
    salesChannelId: entity.salesChannelId,
    salesChannel: entity.salesChannel,
    live: entity.live,
    listingStatus: LISTING_STATUS.PUBLISHED,
  };
  delete restored.draftBaseline;
  delete restored.draftDomains;
  return restored;
};

const collectRemovedIds = ({ ids = [], skipped = [], failed = [] }) => {
  const failedIds = new Set(failed.map(toId));
  const keepSkippedIds = new Set(
    skipped
      .filter((item) => item?.reason && !isUnavailableSkip(item.reason) && !isNoChangeSkip(item.reason))
      .map(toId),
  );

  return ids.filter((id) => {
    const value = String(id);
    return !failedIds.has(value) && !keepSkippedIds.has(value);
  });
};

const prunePinnedSearch = (state, removedListingIds, removedVariantIds) => {
  const pin = state.ui.pinnedSearch;
  if (!pin) return;

  const parents = pin.variantParentById || {};
  const ids = (pin.ids || []).filter((id) => {
    const value = String(id);
    if (removedListingIds.has(value) || removedVariantIds.has(value)) return false;
    const parentId = parents[value];
    return parentId == null || !removedListingIds.has(String(parentId));
  });

  if (!ids.length) {
    state.ui.pinnedSearch = null;
    return;
  }

  const keptIds = new Set(ids.map(String));
  const variantParentById = {};
  Object.keys(parents).forEach((id) => {
    if (keptIds.has(id)) variantParentById[id] = parents[id];
  });

  state.ui.pinnedSearch = {
    ids,
    productLayer: pin.productLayer,
    listingStatus: pin.listingStatus,
    variantParentById,
  };
};

export const discardListingDrafts = createAsyncThunk(
  'products/discardListingDrafts',
  async ({
    productIds = [],
    variantIds = [],
    requestContext = null,
    requestKeys = [],
  }, {
    extra: {
      sdk, Toast, Logger,
    },
  }) => {
    try {
      const { data } = await sdk.products.discardDrafts({
        ...(productIds.length ? { productIds } : {}),
        ...(variantIds.length ? { variantIds } : {}),
      });

      const discarded = toCount(data?.discarded);
      const deleted = toCount(data?.deleted);
      const skipped = Array.isArray(data?.skipped) ? data.skipped : [];
      const failed = Array.isArray(data?.failed) ? data.failed : [];
      const extraProductIds = Array.isArray(data?.extraProductIds) ? data.extraProductIds : [];
      const removedProductIds = [...new Set([
        ...collectRemovedIds({
          ids: productIds,
          skipped,
          failed,
        }),
        ...collectRemovedIds({
          ids: extraProductIds,
          skipped,
          failed,
        }),
      ])];
      const removedVariantIds = collectRemovedIds({
        ids: variantIds,
        skipped,
        failed,
      });
      const removedCount = discarded + deleted;
      const accounted = removedCount + skipped.filter(
        (item) => isUnavailableSkip(item?.reason) || isNoChangeSkip(item?.reason),
      ).length;
      const total = toCount(data?.total) || (productIds.length + variantIds.length);
      const remaining = Math.max(0, total - accounted);

      if (!removedCount) {
        Toast(strings.draftsDiscardedNone, { type: 'error', autoClose: false });
      } else if (remaining) {
        Toast(
          `${strings.draftsDiscardedSuccess(removedCount)} ${strings.draftsDiscardedPartial(remaining)}`,
          { type: 'warning', autoClose: false },
        );
      } else {
        Toast(strings.draftsDiscardedSuccess(removedCount), { type: 'success' });
      }

      return {
        productIds,
        variantIds,
        removedProductIds,
        removedVariantIds,
        requestContext,
        requestKeys,
        data,
      };
    } catch (error) {
      Toast(strings.draftsDiscardedNone, { type: 'error', autoClose: false });
      Logger.error(new Error('Bulk discard listing drafts failed'), { error }, [
        'DiscardListingDraftsFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(discardListingDrafts.fulfilled, (state, { payload }) => {
      const removedListingIds = new Set((payload?.removedProductIds || []).map(String));
      const removedVariantIds = new Set((payload?.removedVariantIds || []).map(String));
      if (!removedListingIds.size && !removedVariantIds.size) {
        state.error = null;
        return;
      }

      prunePinnedSearch(state, removedListingIds, removedVariantIds);

      const requestContext = payload.requestContext;
      const currentContext = state.ui.selection.context;
      const sameDraftContext = requestContext?.type === 'channel'
        && requestContext.recordState === 'draft'
        && currentContext?.type === 'channel'
        && currentContext.recordState === 'draft'
        && String(currentContext.channelId) === String(requestContext.channelId);

      const shouldRemoveItem = (item) => {
        const id = String(item._id);
        const base = item.baseProductId != null ? String(item.baseProductId) : '';
        if (removedListingIds.has(id) || removedListingIds.has(base)) return true;
        if (item.type === 'listingVariant' && removedVariantIds.has(id)) return true;
        return false;
      };

      if (sameDraftContext) {
        const prevLength = state.search.items.length;
        state.search.items = state.search.items.filter((item) => !shouldRemoveItem(item));
        const removedCount = prevLength - state.search.items.length;
        if (removedCount > 0) {
          state.search.total = Math.max(0, (state.search.total || 0) - removedCount);
        }
      }

      const requestKeySet = new Set((payload.requestKeys || []).map(String));
      const keysToRemove = state.ui.selection.keys.filter((key) => {
        if (requestKeySet.size && !requestKeySet.has(String(key))) return false;
        const entry = state.ui.selection.byKey[key];
        if (!entry) return false;
        if (entry.type === 'listing' && removedListingIds.has(String(entry._id))) return true;
        if (entry.type === 'listingVariant') {
          if (removedVariantIds.has(String(entry._id))) return true;
          if (removedListingIds.has(String(entry.baseProductId))) return true;
        }
        return false;
      });

      state.ui.selection = pruneSelection(state.ui.selection, keysToRemove);

      removedListingIds.forEach((listingId) => {
        const listing = state.entities?.listings?.[listingId];
        if (!listing) return;
        if (listing.live) {
          const channelId = getChannelEntryId(listing);
          upsertListing(state, restorePublishedFromLive(listing));
          if (listing.masterProductId && channelId) {
            setActiveTabByProductChannel(
              state,
              listing.masterProductId,
              channelId,
              LISTING_STATUS.PUBLISHED,
            );
          }
          return;
        }
        removeListingChannelEntities(state, listing);
      });

      removedVariantIds.forEach((variantId) => {
        const listingVariant = state.entities?.listingVariants?.[variantId];
        if (!listingVariant) return;
        if (listingVariant.live) {
          upsertListingVariant(state, restorePublishedFromLive(listingVariant));
          return;
        }
        removeListingVariant(state, variantId);
      });

      state.error = null;
    })
    .addCase(discardListingDrafts.rejected, (state, { error }) => {
      state.error = error?.message || 'Cannot discard selected drafts';
    });
};
